import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import type { GameContext } from '@/core/context';
import { SceneKeys } from '@/core/scenes/keys';

/**
 * In-game debug overlay (verification builder). Toggled with F3 or `?debug=1`.
 * Shows fps, per-frame draw calls, texture / listener / timer counts, the active scene, the
 * current → next story flag, weather, seed, player position and camera scroll.
 *
 * Usage: `attachDebugOverlay(scene)` from any scene's `create()` (it removes itself on shutdown),
 * or `installDebugOverlay(game)` once from main.ts to host it on the persistent UI scene.
 * All overlays share one visibility state; only the oldest live overlay renders, so attaching to
 * several scenes never draws twice. Text is live (never baked); labels are ASCII to stay compact.
 */

export interface DebugOverlayOptions {
  /** Initial visibility for this attach (defaults to the shared state / `?debug=1`). */
  visible?: boolean;
  /** Refresh the text every N frames (default 10 ≈ 6 Hz at 60 fps). */
  refreshEveryFrames?: number;
}

export interface DebugOverlayGlobal {
  show(): void;
  hide(): void;
  toggle(): boolean;
  isVisible(): boolean;
}

declare global {
  interface Window { __nantianOverlay?: DebugOverlayGlobal }
}

interface DrawStats { frame: number; last: number }

const REGISTRY_DRAW_CALLS = 'perf.drawCalls';
const shared = { visible: false, urlParsed: false, lastToggleStamp: -1 };
const live = new Set<DebugOverlay>();
const drawCounters = new WeakMap<Phaser.Game, DrawStats>();

function urlWantsOverlay(): boolean {
  if (shared.urlParsed) return shared.visible;
  shared.urlParsed = true;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('debug') === '1') shared.visible = true;
  } catch { /* non-browser */ }
  return shared.visible;
}

function refreshOwnership(): void {
  let owner: DebugOverlay | null = null;
  for (const o of live) { if (!o.destroyed) { owner = o; break; } }
  for (const o of live) o.applyVisibility(shared.visible && o === owner);
}

export function isDebugOverlayVisible(): boolean { return shared.visible; }
export function setDebugOverlayVisible(visible: boolean): void {
  shared.urlParsed = true;
  shared.visible = visible;
  refreshOwnership();
}
export function toggleDebugOverlay(): boolean {
  setDebugOverlayVisible(!shared.visible);
  return shared.visible;
}

/**
 * Count WebGL draw calls per frame by wrapping the renderer's draw entry points (no raw `gl` calls).
 * Publishes the last frame's count to `game.registry` under `perf.drawCalls` so `__nantian.getPerf()`
 * can read it once the integrator wires it (see docs/core-change-requests/verification-builder.md).
 */
export function ensureDrawCallCounter(game: Phaser.Game): DrawStats {
  const existing = drawCounters.get(game);
  if (existing) return existing;
  const stats: DrawStats = { frame: 0, last: -1 };
  const renderer = game.renderer as unknown as Record<string, unknown> | null;
  if (renderer) {
    for (const name of ['drawElements', 'drawInstances']) {
      const orig = renderer[name];
      if (typeof orig !== 'function') continue;
      const fn = orig as (...args: unknown[]) => unknown;
      renderer[name] = function (this: unknown, ...args: unknown[]) { stats.frame++; return fn.apply(this, args); };
    }
    game.events.on(Phaser.Core.Events.PRE_RENDER, () => { stats.frame = 0; });
    game.events.on(Phaser.Core.Events.POST_RENDER, () => {
      stats.last = stats.frame;
      if (game.registry.get(REGISTRY_DRAW_CALLS) !== stats.last) game.registry.set(REGISTRY_DRAW_CALLS, stats.last);
    });
  }
  drawCounters.set(game, stats);
  return stats;
}

export class DebugOverlay {
  readonly scene: Phaser.Scene;
  destroyed = false;
  private readonly text: Phaser.GameObjects.Text;
  private readonly stats: DrawStats;
  private readonly refreshEvery: number;
  private frames = 0;
  private visibleNow = false;

  constructor(scene: Phaser.Scene, opts: DebugOverlayOptions = {}) {
    this.scene = scene;
    this.stats = ensureDrawCallCounter(scene.game);
    this.refreshEvery = Math.max(1, opts.refreshEveryFrames ?? 10);
    this.text = scene.add.text(2, 2, '', {
      fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
      fontSize: '10px',
      color: '#e2c48c',
      backgroundColor: 'rgba(5, 7, 10, 0.82)',
      padding: { x: 3, y: 2 },
      lineSpacing: 1,
    }).setOrigin(0, 0).setScrollFactor(0).setDepth(Depth.UI + 990).setVisible(false);

    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, this.onPostUpdate, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    scene.events.once(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    const kb = scene.input.keyboard;
    if (kb) {
      kb.addCapture('F3');
      kb.on('keydown-F3', this.onToggleKey, this);
    }
    live.add(this);
    if (opts.visible !== undefined) setDebugOverlayVisible(opts.visible);
    else { urlWantsOverlay(); refreshOwnership(); }
    if (!window.__nantianOverlay) {
      window.__nantianOverlay = {
        show: () => setDebugOverlayVisible(true),
        hide: () => setDebugOverlayVisible(false),
        toggle: () => toggleDebugOverlay(),
        isVisible: () => shared.visible,
      };
    }
  }

  /** Called by the shared state; only the owning overlay actually renders. */
  applyVisibility(visible: boolean): void {
    if (this.destroyed) return;
    this.visibleNow = visible;
    this.text.setVisible(visible);
    if (visible) this.render();
  }

  private onToggleKey(event: KeyboardEvent): void {
    // Every active scene's keyboard plugin receives the same DOM event: toggle once per event.
    const stamp = event?.timeStamp ?? -1;
    if (stamp === shared.lastToggleStamp) return;
    shared.lastToggleStamp = stamp;
    toggleDebugOverlay();
  }

  private onPostUpdate(): void {
    if (!this.visibleNow || this.destroyed) return;
    if (++this.frames % this.refreshEvery !== 0) return;
    this.render();
  }

  private render(): void {
    const game = this.scene.game;
    const ctx = this.scene.registry.get('ctx') as GameContext | undefined;
    const api = window.__nantian;
    const fps = Math.round(game.loop.actualFps);
    const draws = this.stats.last < 0 ? '?' : String(this.stats.last);
    const textures = Object.keys(game.textures.list).length;
    const listeners = ctx ? ctx.listenerCount() : -1;
    const timers = ctx ? ctx.timerCount() : -1;
    const active = ctx?.router.current() ?? null;
    const sceneKey = active?.scene.key ?? this.scene.scene.key;
    const cur = ctx?.story.current() ?? '-';
    const next = ctx?.story.next() ?? 'end';
    const weather = ctx?.weather ?? '?';
    const seed = ctx?.seed ?? '?';
    const p = ctx?.position ?? null;
    const pos = p ? `${p.map} ${p.x},${p.y} ${p.facing}` : '-';
    const cam = active?.cameras?.main;
    const camText = cam ? `${Math.round(cam.scrollX)},${Math.round(cam.scrollY)}` : '-';
    const errs = api?.errors.length ?? 0;
    const warns = api?.warnings.length ?? 0;
    const zoom = game.scale.zoom;
    const version = api?.version ?? '?';
    this.text.setText([
      `fps ${fps}  draw ${draws}  tex ${textures}  lsn ${listeners}  tmr ${timers}  err ${errs}  warn ${warns}`,
      `scene ${sceneKey}  flag ${cur} -> ${next}`,
      `weather ${weather}  seed ${seed}  pos ${pos}  cam ${camText}`,
      `v${version}  ${GAME_W}x${GAME_H} x${zoom}  F3 toggle`,
    ]);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    live.delete(this);
    this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, this.onPostUpdate, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.scene.events.off(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    this.scene.input.keyboard?.off('keydown-F3', this.onToggleKey, this);
    this.text.destroy();
    refreshOwnership();
  }
}

/** Attach (or reuse) the overlay on a scene. Safe to call from any scene's `create()`. */
export function attachDebugOverlay(scene: Phaser.Scene, opts: DebugOverlayOptions = {}): DebugOverlay {
  for (const o of live) if (o.scene === scene && !o.destroyed) { if (opts.visible !== undefined) setDebugOverlayVisible(opts.visible); return o; }
  return new DebugOverlay(scene, opts);
}

/**
 * Host the overlay on the persistent UI scene as soon as it is running (call once from main.ts).
 * Keeps a cheap per-step check so the overlay comes back if the UI scene is ever restarted.
 */
export function installDebugOverlay(game: Phaser.Game): void {
  const check = () => {
    if (!game.scene.isActive(SceneKeys.UI)) return;
    const ui = game.scene.getScene(SceneKeys.UI);
    if (!ui) return;
    for (const o of live) if (o.scene === ui && !o.destroyed) return;
    attachDebugOverlay(ui);
  };
  game.events.on(Phaser.Core.Events.POST_STEP, check);
}
